import { useEffect, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Users, Clock, BookOpen, TrendingUp, ArrowDown } from 'lucide-react'
import { metricsApiClient } from '../../../api/http/wireApiClient'
import type { ArticleActivityEntry, ArticleNavMethodsByArticleEntry } from '../../metrics/types'
import { dateToApi, nDaysAgoStr, todayStr } from '../../metrics/helpers'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

function fmtNavMethod(method: string): string {
  if (!method || method.toUpperCase() === 'UNKNOWN') return 'Direct Navigation'
  return method
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

const NAV_METHOD_COLORS = [
  '#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#db2777', '#7c3aed', '#6366f1', '#84cc16',
]

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#e5e7eb',
      fgSecondary: '#9ca3af',
      line: '#374151',
      tooltipBg: '#111827',
      tooltipFg: '#f9fafb',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

// ── Stat row ──────────────────────────────────────────────────────────────────

function StatRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType
  label: string
  value: string
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <div className="flex items-center gap-1.5 text-fg-secondary">
        <Icon size={11} aria-hidden="true" />
        <span className="text-[11px]">{label}</span>
      </div>
      <span className="text-[13px] font-semibold tabular-nums text-fg">{value}</span>
    </div>
  )
}

// ── ArticleAdminRail ──────────────────────────────────────────────────────────

interface ArticleAdminRailProps {
  articleId: string
  onAvgScrollDepth?: (depthPct: number | null) => void
}

export function ArticleAdminRail({ articleId, onAvgScrollDepth }: ArticleAdminRailProps) {
  const [rows, setRows] = useState<ArticleActivityEntry[]>([])
  const [discoveryData, setDiscoveryData] = useState<ArticleNavMethodsByArticleEntry | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)

  const chartColors = resolveChartColors()

  useEffect(() => {
    if (!articleId) return
    setLoading(true)
    setError(null)
    const from = dateToApi(nDaysAgoStr(90), false)
    const to   = dateToApi(todayStr(), true)
    metricsApiClient
      .get<ArticleActivityEntry[]>(`/metrics/article/activity/per-article/${articleId}`, {
        params: { from, to },
      })
      .then(({ data }) => {
        const entries = data ?? []
        setRows(entries)
        if (entries.length > 0) {
          const depth = Math.round(entries.reduce((s, r) => s + r.maxScrollYIndex, 0) / entries.length)
          onAvgScrollDepth?.(depth)
        } else {
          onAvgScrollDepth?.(null)
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load metrics'))
      .finally(() => setLoading(false))

    setDiscoveryError(null)
    metricsApiClient
      .get<ArticleNavMethodsByArticleEntry>(`/metrics/nav-methods/${articleId}`, {
        params: { from, to },
      })
      .then(({ data }) => setDiscoveryData(data ?? null))
      .catch((e: unknown) => {
        setDiscoveryData(null)
        setDiscoveryError(e instanceof Error ? e.message : 'Failed to load discovery metrics')
      })
  }, [articleId])

  // ── Derived stats ────────────────────────────────────────────────────────────
  const totalReads    = rows.length
  const uniqueReaders = new Set(rows.map(r => r.clientDn)).size
  const totalTimeMs   = rows.reduce((s, r) => s + r.timeSpentMs, 0)
  const avgTimeMs     = totalReads > 0 ? Math.round(totalTimeMs / totalReads) : 0
  const avgScrollDepth = totalReads > 0
    ? Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / totalReads)
    : 0

  const sortedMethods = [...(discoveryData?.navMethods ?? [])].sort((a, b) => b.views - a.views)
  const navPieOption = sortedMethods.length === 0 ? null : {
    backgroundColor: 'transparent',
    textStyle: { color: chartColors.fg },
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: chartColors.tooltipBg,
      borderColor: chartColors.line,
      textStyle: { color: chartColors.tooltipFg, fontSize: 11 },
      formatter: (p: { name: string; value: number; percent: number }) =>
        `${p.name}: <b>${p.value.toLocaleString()}</b> (${p.percent}%)`,
    },
    legend: {
      orient: 'vertical' as const,
      right: 0,
      top: 'middle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: chartColors.fgSecondary, fontSize: 10 },
    },
    series: [{
      type: 'pie' as const,
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      data: sortedMethods.map((m, i) => ({
        name: fmtNavMethod(m.method),
        value: m.views,
        itemStyle: { color: NAV_METHOD_COLORS[i % NAV_METHOD_COLORS.length] },
      })),
      label: { show: false },
      emphasis: { label: { show: false } },
    }],
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Engagement · Last 90 days
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[11px] text-fg-secondary">
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-line border-t-fg-secondary" />
          Loading…
        </div>
      )}

      {error && !loading && (
        <p className="text-[11px] text-error-fg">{error}</p>
      )}

      {!loading && !error && rows.length === 0 && (
        <p className="text-[11px] text-fg-secondary opacity-60">No activity in the last 90 days.</p>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="divide-y divide-line rounded-lg border border-line bg-canvas px-3">
          <StatRow icon={BookOpen}   label="Reads"           value={totalReads.toLocaleString()} />
          <StatRow icon={Users}      label="Unique readers"  value={uniqueReaders.toLocaleString()} />
          <StatRow icon={Clock}      label="Avg time/read"   value={fmtDuration(avgTimeMs)} />
          <StatRow icon={TrendingUp} label="Total time"      value={fmtDuration(totalTimeMs)} />
          <StatRow icon={ArrowDown}  label="Avg scroll"      value={`${avgScrollDepth}%`} />
        </div>
      )}

      {discoveryError && (
        <p className="text-[11px] text-error-fg">{discoveryError}</p>
      )}

      {navPieOption && (
        <div>
          <p className="mb-2 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
            How Readers Arrived
          </p>
          <div className="rounded-lg border border-line bg-canvas p-3">
            <ReactECharts option={navPieOption} style={{ height: 220 }} />
          </div>
        </div>
      )}
    </div>
  )
}
